import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, 
  User, 
  ShieldCheck, 
  Bell, 
  Trash2, 
  Smartphone, 
  Laptop, 
  Lock, 
  CheckCircle2, 
  AlertTriangle, 
  LogOut,
  Sparkles
} from 'lucide-react';
import { useStore } from '../store/useStore';

export const SettingsPage: React.FC = () => {
  const { user, logout } = useStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'privacy'>('security');

  const [emailAlerts, setEmailAlerts] = useState(true);
  const [smsAlerts, setSmsAlerts] = useState(true);
  const [pushAlerts, setPushAlerts] = useState(true);
  const [monthlyDigest, setMonthlyDigest] = useState(true);

  const [savedSuccess, setSavedSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const loginHistory = [
    { device: 'macOS Chrome 132', location: 'Sydney, NSW, Australia', ip: '110.142.78.21', date: 'Today at 11:45 AM', current: true },
    { device: 'iPhone 16 Safari', location: 'Sydney, NSW, Australia', ip: '1.144.112.90', date: 'Yesterday at 8:20 PM', current: false },
    { device: 'Windows 11 Edge', location: 'Melbourne, VIC, Australia', ip: '120.18.99.14', date: '12 August 2026', current: false }
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Account Settings & Security</h1>
        <p className="text-xs text-slate-400 mt-1">Manage credentials, two-factor authentication, active devices, and privacy options.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-3">
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
            activeTab === 'security' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          Security & Devices
        </button>
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
            activeTab === 'profile' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <User className="w-4 h-4" />
          Profile & Identifiers
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
            activeTab === 'notifications' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Bell className="w-4 h-4" />
          Notifications
        </button>
        <button
          onClick={() => setActiveTab('privacy')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors ${
            activeTab === 'privacy' ? 'bg-rose-950 text-rose-300 border border-rose-800' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Trash2 className="w-4 h-4" />
          Data & Deletion
        </button>
      </div>

      {savedSuccess && (
        <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-700 text-emerald-200 text-xs flex items-center gap-2 animate-fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>Settings successfully saved and synchronized.</span>
        </div>
      )}

      {/* Tab: Security & Devices */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* MFA Card */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-950 text-emerald-400 border border-emerald-800">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Two-Factor Authentication (MFA)</h3>
                  <p className="text-xs text-slate-400">Protects your IDFraud.au dashboard against credential attacks</p>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
                ACTIVE
              </span>
            </div>

            <p className="text-xs text-slate-300">
              Your account is secured with Authenticator App 2FA (TOTP). Codes are generated on your physical smartphone.
            </p>
          </div>

          {/* Active Sessions Table */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-bold text-white">Active Login Sessions & Devices</h3>
                <p className="text-xs text-slate-400">Authorized devices currently connected to your account</p>
              </div>
              <button
                onClick={() => alert('All other device sessions have been revoked.')}
                className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-rose-300 text-xs font-semibold transition-colors self-start sm:self-auto"
              >
                Log Out of All Other Devices
              </button>
            </div>

            <div className="divide-y divide-slate-800/80 text-xs">
              {loginHistory.map((sess, idx) => (
                <div key={idx} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Laptop className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div>
                      <div className="font-bold text-white flex items-center gap-2">
                        <span>{sess.device}</span>
                        {sess.current && (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-950 text-emerald-400 font-mono">THIS DEVICE</span>
                        )}
                      </div>
                      <div className="text-slate-400 font-mono text-[11px]">{sess.location} • {sess.ip}</div>
                    </div>
                  </div>
                  <span className="text-slate-500 font-mono">{sess.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Profile */}
      {activeTab === 'profile' && (
        <form onSubmit={handleSave} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block font-semibold text-slate-300">First Name:</label>
              <input
                type="text"
                defaultValue={user?.firstName || 'Alex'}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white"
              />
            </div>
            <div className="space-y-1">
              <label className="block font-semibold text-slate-300">Last Name:</label>
              <input
                type="text"
                defaultValue={user?.lastName || 'Mitchell'}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block font-semibold text-slate-300">Primary Email:</label>
            <input
              type="email"
              defaultValue={user?.email || 'alex.mitchell@sydney-design.com.au'}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white"
            />
          </div>

          <div className="space-y-1">
            <label className="block font-semibold text-slate-300">Australian Mobile Phone (for critical SMS alerts):</label>
            <input
              type="text"
              defaultValue={user?.phone || '+61 412 890 341'}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white"
            />
          </div>

          <div className="pt-3">
            <button
              type="submit"
              className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-colors"
            >
              Save Profile Changes
            </button>
          </div>
        </form>
      )}

      {/* Tab: Notifications */}
      {activeTab === 'notifications' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 text-xs">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Alert Notification Channels</h3>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-3.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
              <div>
                <div className="font-bold text-white">Instant Email Threat Alerts</div>
                <p className="text-slate-400 text-[11px]">Receive 🚨 New Identity Alert whenever credentials appear in breaches.</p>
              </div>
              <input
                type="checkbox"
                checked={emailAlerts}
                onChange={(e) => setEmailAlerts(e.target.checked)}
                className="w-4 h-4 rounded accent-emerald-600"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
              <div>
                <div className="font-bold text-white">Critical SMS Alerts</div>
                <p className="text-slate-400 text-[11px]">Dispatch urgent SMS for high-severity fraudulent loan inquiries or stolen ID.</p>
              </div>
              <input
                type="checkbox"
                checked={smsAlerts}
                onChange={(e) => setSmsAlerts(e.target.checked)}
                className="w-4 h-4 rounded accent-emerald-600"
              />
            </label>

            <label className="flex items-center justify-between p-3.5 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer">
              <div>
                <div className="font-bold text-white">Monthly Security Score Digest</div>
                <p className="text-slate-400 text-[11px]">Summary of your score progression and new Australian scam trends.</p>
              </div>
              <input
                type="checkbox"
                checked={monthlyDigest}
                onChange={(e) => setMonthlyDigest(e.target.checked)}
                className="w-4 h-4 rounded accent-emerald-600"
              />
            </label>
          </div>

          <button
            onClick={() => {
              setSavedSuccess(true);
              setTimeout(() => setSavedSuccess(false), 2000);
            }}
            className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold transition-colors"
          >
            Save Notification Preferences
          </button>
        </div>
      )}

      {/* Tab: Data & Deletion */}
      {activeTab === 'privacy' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 text-xs">
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Data Portability & Account Purge</h3>
            <p className="text-slate-400">
              Under Australian Privacy Principles, you maintain full control over all personal identifiers and telemetry.
            </p>
          </div>

          <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-4">
            <div>
              <div className="font-bold text-white">Export My Monitoring Data (JSON)</div>
              <p className="text-slate-400 text-[11px]">Download all active monitors, document logs, and alert history.</p>
            </div>
            <button
              onClick={() => alert('Data export package downloaded.')}
              className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold"
            >
              Download Export
            </button>
          </div>

          <div className="p-6 rounded-2xl bg-rose-950/20 border border-rose-800/60 space-y-3">
            <h4 className="font-bold text-rose-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              <span>Danger Zone: Permanent Account Deletion</span>
            </h4>
            <p className="text-slate-300 leading-relaxed">
              Permanently delete your profile, cancel active monitoring, and purge all hashes from our servers. This action is irreversible.
            </p>

            {showDeleteConfirm ? (
              <div className="pt-2 flex items-center gap-3">
                <button
                  onClick={() => {
                    logout();
                    alert('Account has been permanently deleted.');
                    window.location.href = '/';
                  }}
                  className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold"
                >
                  Yes, Permanently Delete All My Data
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 rounded-xl bg-rose-900/60 hover:bg-rose-900 text-rose-200 font-bold border border-rose-700"
              >
                Request Account Deletion
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
